/**
 * What the progress tracker needs from any player: whole seconds watched, and the end. Local
 * lessons play in a <video>; Bunny Stream lessons play in Bunny's iframe, driven via player.js.
 */
export type PlaybackClock = {
  subscribe(handlers: {
    /** Whole seconds into the video, plus its length once the player knows it (LRN-19). */
    onTime: (seconds: number, durationSec?: number) => void;
    onEnded: (durationSec: number) => void;
    /** H3: the student pressed play (one video at a time). */
    onPlay?: () => void;
    /** H3: playback stopped (paused or ended). */
    onPause?: () => void;
  }): () => void;
  /**
   * LRN-10: jump to a position (resume / start over). Waits until the player can seek. A seek is
   * not watching — the server credits watch time by wall-clock plausibility (LRN-11).
   */
  seek(seconds: number): void;
  /** H3: pause here when the account plays on another device. */
  pause?(): void;
  /** H3: "Play here". */
  play?(): void;
};

/** HTMLMediaElement.HAVE_METADATA — jsdom does not define the constants on the element. */
const HAVE_METADATA = 1;

export function htmlVideoClock(video: HTMLVideoElement): PlaybackClock {
  return {
    subscribe({ onTime, onEnded, onPlay, onPause }) {
      const time = () =>
        onTime(
          Math.floor(video.currentTime),
          Number.isFinite(video.duration) && video.duration > 0
            ? Math.floor(video.duration)
            : undefined,
        );
      const ended = () => {
        onPause?.();
        onEnded(Math.floor(video.duration || video.currentTime));
      };
      const played = () => onPlay?.();
      const paused = () => onPause?.();
      video.addEventListener("timeupdate", time);
      video.addEventListener("ended", ended);
      video.addEventListener("play", played);
      video.addEventListener("pause", paused);
      return () => {
        video.removeEventListener("timeupdate", time);
        video.removeEventListener("ended", ended);
        video.removeEventListener("play", played);
        video.removeEventListener("pause", paused);
      };
    },
    pause() {
      video.pause();
    },
    play() {
      // A refused play (autoplay policy) just leaves the player paused.
      void Promise.resolve(video.play()).catch(() => undefined);
    },
    seek(seconds) {
      const apply = () => {
        video.currentTime = Math.max(0, seconds);
      };
      if (video.readyState >= HAVE_METADATA) apply();
      else video.addEventListener("loadedmetadata", apply, { once: true });
    },
  };
}

export type PlayerJsLike = {
  on(event: string, callback: (data?: unknown) => void): void;
  off(event: string, callback?: (data?: unknown) => void): void;
  setCurrentTime(seconds: number): void;
  play?(): void;
  pause?(): void;
  /** player.js sets this once the iframe's player has said "ready". */
  isReady?: boolean;
};

export function playerJsClock(player: PlayerJsLike): PlaybackClock {
  return {
    subscribe({ onTime, onEnded, onPlay, onPause }) {
      let duration = 0;
      const time = (data?: unknown) => {
        const { seconds, duration: total } = (data ?? {}) as {
          seconds?: unknown;
          duration?: unknown;
        };
        if (typeof total === "number") duration = total;
        if (typeof seconds === "number") {
          onTime(Math.floor(seconds), duration > 0 ? Math.floor(duration) : undefined);
        }
      };
      const ended = () => {
        onPause?.();
        onEnded(Math.floor(duration));
      };
      const played = () => onPlay?.();
      const paused = () => onPause?.();
      player.on("timeupdate", time);
      player.on("ended", ended);
      player.on("play", played);
      player.on("pause", paused);
      return () => {
        player.off("timeupdate", time);
        player.off("ended", ended);
        player.off("play", played);
        player.off("pause", paused);
      };
    },
    pause() {
      player.pause?.();
    },
    play() {
      player.play?.();
    },
    seek(seconds) {
      const apply = () => player.setCurrentTime(Math.max(0, seconds));
      if (player.isReady) apply();
      else player.on("ready", apply);
    },
  };
}
